import { ErrorState, Icon, Spinner } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbBed } from "react-icons/tb";
import { z } from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import { HotelCapacityMonthGrid } from "@/pages/hotel-capacity/components/hotel-capacity-month-grid";
import { HotelCapacityMonthSelect } from "@/pages/hotel-capacity/components/hotel-capacity-month-select";
import { HotelSelector } from "@/pages/hotel-capacity/components/hotel-selector";
import { useHotelCapacity, useHotels } from "@/queries/hotel.queries";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

const HotelCapacitySearchSchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.HOTEL_CAPACITY)({
    component: HotelCapacityPage,
    validateSearch: searchValidator(HotelCapacitySearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "hotel_capacity_title",
                to: APP_ROUTES_TO.HOTEL_CAPACITY,
            },
            title: "hotel_capacity_title",
        },
        permissions: [PERMISSIONS.CALL_HOTEL_BASE],
    },
});

function HotelCapacityPage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });
    const {
        data: hotels = [],
        isLoading: isLoadingHotels,
        isError: isHotelsError,
        refetch: refetchHotels,
    } = useHotels();
    const [selectedHotelId, setSelectedHotelId] = useState<string>();

    const now = DateTime.now();
    const year = search.year ?? now.year;
    const month = search.month ?? now.month;
    const selectedMonth = useMemo(() => DateTime.fromObject({ year, month, day: 1 }), [year, month]);

    const selectedHotel = hotels.find((hotel) => hotel.id === selectedHotelId) ?? hotels[0];

    const {
        data: capacity,
        isLoading: isLoadingCapacity,
        isError: isCapacityError,
        refetch: refetchCapacity,
    } = useHotelCapacity(selectedHotel?.id, year, month);

    useEffect(() => {
        if (search.year === year && search.month === month) {
            return;
        }

        navigate({
            search: {
                year,
                month,
            },
            replace: true,
        });
    }, [month, navigate, search.month, search.year, year]);

    const handleMonthChange = (nextMonth: DateTime) => {
        navigate({
            search: {
                year: nextMonth.year,
                month: nextMonth.month,
            },
        });
    };

    return (
        <div className="flex h-full w-full flex-col gap-4 overflow-auto p-4">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Icon icon={TbBed} size={18} className="text-primary" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold">{t("hotel_capacity_title")}</h1>
                        <p className="text-xs text-muted-foreground">
                            {selectedHotel
                                ? t("hotel_capacity_subtitle_with_hotel", { hotel: selectedHotel.name })
                                : t("hotel_capacity_subtitle")}
                        </p>
                    </div>
                </div>

                {isHotelsError ? (
                    <ErrorState
                        title={t("hotel_capacity_load_error")}
                        message={t("hotel_capacity_load_error_message")}
                        onRetry={() => refetchHotels()}
                    />
                ) : isLoadingHotels ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner />
                    </div>
                ) : hotels.length === 0 ? (
                    <div className="rounded-xl border border-border/70 bg-background/80 px-4 py-8 text-center text-sm text-muted-foreground">
                        {t("hotel_capacity_no_hotels")}
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <HotelSelector
                                hotels={hotels}
                                selectedHotelId={selectedHotel?.id}
                                onHotelChange={setSelectedHotelId}
                            />
                            <div className="w-full sm:max-w-md">
                                <HotelCapacityMonthSelect
                                    selectedMonth={selectedMonth}
                                    onMonthChange={handleMonthChange}
                                />
                            </div>
                        </div>

                        {isCapacityError ? (
                            <ErrorState
                                title={t("hotel_capacity_load_error")}
                                message={t("hotel_capacity_load_error_message")}
                                onRetry={() => refetchCapacity()}
                            />
                        ) : isLoadingCapacity || !capacity ? (
                            <div className="flex items-center justify-center py-16">
                                <Spinner />
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/80" />
                                        {t("hotel_capacity_legend_available")}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="h-2.5 w-2.5 rounded-sm bg-amber-500/80" />
                                        {t("hotel_capacity_legend_low")}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="h-2.5 w-2.5 rounded-sm bg-destructive/80" />
                                        {t("hotel_capacity_legend_full")}
                                    </span>
                                </div>
                                <HotelCapacityMonthGrid capacity={capacity} />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
